import { ENV } from '@/environment';
import store from '../store';
import lite from '../store/shortcake-lite';

const getVideoTitle = ({ name, items }) => {
  return `${name}/${items.length}`;
};

const get = path => fetch(`${ENV.S3_ROOT}${path}?t=${new Date().getTime()}`).then(r => r.json());

export default {
  // Remote
  fetchToc: () => {
    // Don't filter here - otherwise the kit pickers cannot
    // be accessed even if going directly to them.
    return get('/toc.json').then(toc => {
      return toc.map(t => ({
        ...t,
        tag: t.tag || (t.limited ? 'Limited Edition' : null),
      }));
    });
  },
  fetchHelp: () => {
    return get('/help.json').then(({ toc_group_order: ord, ...topics }) => {
      // Sort just once after fetch based on TOC order
      return Object.entries(topics)
        .reduce((acc, [, topic]) => {
          // Adjust the titles here
          const fixedTopic = {
            ...topic,
            topics: topic.topics?.map(t => ({
              ...t,
              microsteps: t.microsteps?.map(ms => ({
                ...ms,
                display_name: getVideoTitle({
                  name: ms.display_name,
                  items: t.microsteps,
                }),
              })),
            })),
            multiTopic: topic.topics?.length > 1,
          };

          return [...acc, fixedTopic];
        }, [])
        .sort((a, b) => ord.indexOf(a.id) - ord.indexOf(b.id));
    });
  },
  fetchTutorial: id => {
    return get(`/${id}.json`).then(r => {
      // Check if we have a light version
      const liteVersion = lite.find(l => {
        return l.id === id || l.id_prod === id;
      });

      if (liteVersion) {
        return Promise.resolve({
          ...r,
          ...liteVersion,
          sections: [],
          isLite: true,
        });
      }

      return {
        ...r,
        sections: (r.sections || []).map(s => ({
          ...s,
          steps: s.steps.map(st => ({
            ...st,
            microsteps: st.microsteps?.map(ms => ({
              ...ms,
              display_name: getVideoTitle({
                name: ms.display_name,
                items: st.microsteps,
              }),
              videos:
                ms.videos?.length > 0
                  ? ms.videos
                  : [
                      {
                        id: 'no-video',
                      },
                    ],
            })),
          })),
        })),
      };
    });
  },
  // Local
  findSectionById({ tutorial, sectionId }) {
    return tutorial.sections.find(s => s.id === sectionId);
  },
  findSectionWithOffsetFromId({ tutorial, sectionId, offset }) {
    return tutorial.sections[tutorial.sections.findIndex(s => s.id === sectionId) + offset];
  },
  getAllSteps({ tutorial }) {
    return tutorial.sections
      .map(section =>
        section.steps.map(step => {
          return { ...step, sectionId: section.id };
        }),
      )
      .flat();
  },
  findStepById({ tutorial, stepId }) {
    return this.getAllSteps({ tutorial }).find(step => step.id == stepId);
  },
  findStepWithOffsetFromId({ tutorial, stepId, offset }) {
    const allSteps = this.getAllSteps({ tutorial });
    const targetIndex = allSteps.findIndex(step => step.id == stepId);
    const index = targetIndex + offset;

    // reached the beginning
    if (index < 0) return allSteps[0];

    return allSteps[index];
  },
  getTutorialBySlug(slug) {
    return store.state.tutorials.find(x => x.slug_url === slug);
  },
  getFullTutorialBySlug(slug) {
    return store.state.fullTutorials[slug];
  },
  isStepFirstInGroup({ tutorial, step }) {
    if (!step) return false;

    const section = this.findSectionById({ tutorial, sectionId: step.sectionId });

    return section?.steps.findIndex(x => x.id === step.id) === 0;
  },
  getHelp(helpCodes) {
    let helpTopics = store.state.help ?? [];

    if (helpCodes?.length > 0) {
      // We only want specific help codes
      helpTopics = helpTopics.filter(h => helpCodes.includes(h.id));
    }

    return helpTopics;
  },
  findHelpTopicById(id) {
    const topics = store.state.help ?? [];

    for (let t = 0; t < topics.length; t++) {
      const parent = topics[t];

      // This is a parent topic, exit
      if (parent.id === id) {
        return { parent };
      }

      const child = parent?.topics.find(c => c.id === id);

      // We found the child, exit
      if (child) {
        return { parent, child };
      }
    }
  },
};
